'use client';

import React, { useMemo, useState, useCallback } from 'react';
import {
  RefreshCw,
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateRangePicker, getParisCurrentWeek, formatParisDate } from '@/components/ui/date-range-picker';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Treemap,
} from 'recharts';
import type { DateRange } from 'react-day-picker';
import { trpc } from '@/lib/api/trpc';
import { formatMoney } from '@/lib/money';
import { dollarsToCents } from '@tagadapay/billing-core';
import { useTranslation } from 'react-i18next';
import { useUrlDateRange } from '@/hooks/use-url-date-range';
import { isTagadaPay } from '@/config/branding';
import { Navigate } from 'react-router-dom';

const REVENUE_COLORS = {
  tagadaPay: '#3b82f6',
  reseller: '#22c55e',
  whitelabelDeploy: '#f97316',
  mor: '#a855f7',
  partnerCrm: '#ec4899',
  adyen: '#14b8a6',
  eventFees: '#eab308',
} as const;

const PIE_COLORS = ['#3b82f6', '#22c55e', '#f97316', '#a855f7', '#14b8a6', '#eab308'];
const TAGADA_ACCOUNT_COLOR = '#3b82f6';
const PARTNER_ACCOUNT_COLORS = ['#22c55e', '#f97316', '#a855f7', '#14b8a6', '#eab308', '#ec4899'];

/** Tagada accounts in blue; each partner slug gets a stable palette colour (no slug list in the bundle). */
const partnerAccountColor = (partner: string | null | undefined): string => {
  if (!partner || partner === 'tagada') return TAGADA_ACCOUNT_COLOR;
  let hash = 0;
  for (const ch of partner) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return PARTNER_ACCOUNT_COLORS[hash % PARTNER_ACCOUNT_COLORS.length];
};

function RevenueOverTimeChart({
  data,
  isLoading,
}: {
  data: Array<{
    periodStart: string;
    tagadaRevenue: {
      tagadaCustomerFeesCents: number;
      fmjRevenueCents: number;
      appticsRevenueCents: number;
      // Null for partner-scoped queries; this chart is global so in
      // practice it is always a number, but the type stays nullable
      // to match the canonical TagadaRevenueData shape.
      morNetFeesCents: number | null;
      partnerResoldCrmCents: number;
      adyenNetCents?: number;
      eventFeesCents?: number;
      totalRevenueCents: number;
    };
  }>;
  isLoading: boolean;
}) {
  const chartData = useMemo(() => {
    return data.map((week) => {
      const start = new Date(week.periodStart);
      const label = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return {
        week: label,
        'TagadaPay': week.tagadaRevenue.tagadaCustomerFeesCents / 100,
        'Reseller': week.tagadaRevenue.fmjRevenueCents / 100,
        'White-label deploy': week.tagadaRevenue.appticsRevenueCents / 100,
        'TagadaPayment': (week.tagadaRevenue.morNetFeesCents ?? 0) / 100,
        'Partner CRM': (week.tagadaRevenue.partnerResoldCrmCents ?? 0) / 100,
        'Adyen': (week.tagadaRevenue.adyenNetCents ?? 0) / 100,
        'Event fees': (week.tagadaRevenue.eventFeesCents ?? 0) / 100,
        total: week.tagadaRevenue.totalRevenueCents / 100,
      };
    });
  }, [data]);

  const { t } = useTranslation('analytics');

  if (isLoading) {
    return <Skeleton className="h-[350px] w-full" />;
  }

  if (chartData.length === 0) {
    return (
      <div className="text-muted-foreground flex h-[350px] items-center justify-center text-sm">
        {t('adminRevenue.noRevenueHistory')}
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={350}>
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis dataKey="week" tick={{ fontSize: 12 }} className="text-muted-foreground" />
        <YAxis tick={{ fontSize: 12 }} className="text-muted-foreground" tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
        <Tooltip
          formatter={(value: number, name: string) => [`$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, name]}
          labelFormatter={(label: string) => t('adminRevenue.weekOf', { label })}
          contentStyle={{ borderRadius: '8px', fontSize: '13px' }}
        />
        <Legend />
        <Bar dataKey="TagadaPay" stackId="revenue" fill={REVENUE_COLORS.tagadaPay} radius={[0, 0, 0, 0]} />
        <Bar dataKey="Reseller" stackId="revenue" fill={REVENUE_COLORS.reseller} />
        <Bar dataKey="White-label deploy" stackId="revenue" fill={REVENUE_COLORS.whitelabelDeploy} />
        <Bar dataKey="TagadaPayment" stackId="revenue" fill={REVENUE_COLORS.mor} />
        <Bar dataKey="Partner CRM" stackId="revenue" fill={REVENUE_COLORS.partnerCrm} />
        <Bar dataKey="Adyen" stackId="revenue" fill={REVENUE_COLORS.adyen} />
        <Bar dataKey="Event fees" stackId="revenue" fill={REVENUE_COLORS.eventFees} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function PartnerBreakdown({
  summaries,
  isLoading,
}: {
  summaries: Array<{
    partner: string;
    partnerName: string;
    accountCount: number;
    approved: number;
    declined: number;
    estimatedFees: number;
    enhancedFees: number;
  }>;
  isLoading: boolean;
}) {
  const { t } = useTranslation('analytics');
  const total = summaries.reduce((s, p) => s + p.estimatedFees, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{t('adminRevenue.partnerFeeBreakdown')}</CardTitle>
        <CardDescription>{t('adminRevenue.partnerFeeDescription')}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : summaries.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center text-sm">{t('adminRevenue.noPartnerData')}</p>
        ) : (
          <div className="space-y-4">
            {summaries.map((partner) => {
              const pct = total > 0 ? (partner.estimatedFees / total) * 100 : 0;
              return (
                <div key={partner.partner} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{partner.partnerName}</span>
                      <Badge variant="secondary" className="text-xs">
                        {t('adminRevenue.accountsCount', { count: partner.accountCount })}
                      </Badge>
                    </div>
                    <span className="text-sm font-semibold">
                      {formatMoney(partner.estimatedFees, 'USD')}
                    </span>
                  </div>
                  <Progress value={pct} className="h-2" />
                  <div className="text-muted-foreground flex justify-between text-xs">
                    <span>{t('adminRevenue.ofTotal', { pct: pct.toFixed(1) })}</span>
                    <span>
                      {t('adminRevenue.approvedDeclined', {
                        approved: partner.approved.toLocaleString(),
                        declined: partner.declined.toLocaleString(),
                      })}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function getApprovalRateColor(rate: number): string {
  if (rate >= 60) return 'text-emerald-600';
  if (rate >= 30) return 'text-amber-600';
  return 'text-red-600';
}

// --- Revenue Composition Donut ---
function RevenueDonut({
  tagadaRevenue,
  isLoading,
}: {
  tagadaRevenue: {
    tagadaCustomerFeesCents: number;
    fmjRevenueCents: number;
    appticsRevenueCents: number;
    morNetFeesCents: number | null;
    adyenNetCents?: number;
    eventFeesCents?: number;
  } | undefined;
  isLoading: boolean;
}) {
  const data = useMemo(() => {
    if (!tagadaRevenue) return [];
    return [
      { name: 'TagadaPay', value: tagadaRevenue.tagadaCustomerFeesCents / 100 },
      { name: 'Reseller', value: tagadaRevenue.fmjRevenueCents / 100 },
      { name: 'White-label deploy', value: tagadaRevenue.appticsRevenueCents / 100 },
      { name: 'TagadaPayment', value: (tagadaRevenue.morNetFeesCents ?? 0) / 100 },
      { name: 'Adyen', value: (tagadaRevenue.adyenNetCents ?? 0) / 100 },
      { name: 'Event fees', value: (tagadaRevenue.eventFeesCents ?? 0) / 100 },
    ].filter(d => d.value > 0);
  }, [tagadaRevenue]);

  const { t } = useTranslation('analytics');

  if (isLoading) return <Skeleton className="h-[300px] w-full" />;
  if (data.length === 0) {
    return <div className="text-muted-foreground flex h-[300px] items-center justify-center text-sm">{t('adminRevenue.noData')}</div>;
  }

  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data}
          cx="50%"
          cy="50%"
          innerRadius={60}
          outerRadius={100}
          paddingAngle={3}
          dataKey="value"
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip
          formatter={(value: number, name: string) => {
            const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
            return [`$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (${pct}%)`, name];
          }}
          contentStyle={{ borderRadius: '8px', fontSize: '13px' }}
        />
      </PieChart>
    </ResponsiveContainer>
  );
}

// --- Revenue Mix Over Time (Stacked AreaChart) ---
type MapEntry = Record<string, { revenue?: number; amount?: number; saleCount?: number; transactionCount?: number }>;

function RevenueMixChart({
  straightSaleData,
  rebillData,
  upsellData,
  isLoading,
}: {
  straightSaleData: MapEntry | undefined;
  rebillData: MapEntry | undefined;
  upsellData: MapEntry | undefined;
  isLoading: boolean;
}) {
  const chartData = useMemo(() => {
    const allDates = new Set<string>();
    if (straightSaleData) Object.keys(straightSaleData).forEach(d => allDates.add(d));
    if (rebillData) Object.keys(rebillData).forEach(d => allDates.add(d));
    if (upsellData) Object.keys(upsellData).forEach(d => allDates.add(d));

    return Array.from(allDates).sort().map(date => ({
      date,
      label: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      'Straight Sale': (straightSaleData?.[date]?.revenue ?? 0) / 100,
      'Rebill': (rebillData?.[date]?.amount ?? 0) / 100,
      'Upsell': (upsellData?.[date]?.revenue ?? 0) / 100,
    }));
  }, [straightSaleData, rebillData, upsellData]);

  const { t } = useTranslation('analytics');

  if (isLoading) return <Skeleton className="h-[300px] w-full" />;
  if (chartData.length === 0) {
    return <div className="text-muted-foreground flex h-[300px] items-center justify-center text-sm">{t('adminRevenue.noData')}</div>;
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={chartData}>
        <defs>
          <linearGradient id="gradStraight" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="gradRebill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="gradUpsell" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#f97316" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} className="text-muted-foreground" interval="preserveStartEnd" />
        <YAxis tick={{ fontSize: 11 }} className="text-muted-foreground" tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
        <Tooltip
          formatter={(value: number, name: string) => [`$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, name]}
          contentStyle={{ borderRadius: '8px', fontSize: '13px' }}
        />
        <Legend />
        <Area type="monotone" dataKey="Straight Sale" stackId="1" stroke="#3b82f6" fill="url(#gradStraight)" />
        <Area type="monotone" dataKey="Rebill" stackId="1" stroke="#22c55e" fill="url(#gradRebill)" />
        <Area type="monotone" dataKey="Upsell" stackId="1" stroke="#f97316" fill="url(#gradUpsell)" />
      </AreaChart>
    </ResponsiveContainer>
  );
}

// --- Account Concentration Treemap ---
interface TreemapContentProps {
  x: number;
  y: number;
  width: number;
  height: number;
  name: string;
  fees: string;
  fill: string;
}

function TreemapContent({ x, y, width, height, name, fees, fill }: TreemapContentProps) {
  if (width < 50 || height < 30) return null;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} fill={fill} stroke="#fff" strokeWidth={2} rx={4} />
      <text x={x + 6} y={y + 16} fontSize={11} fontWeight={600} fill="#fff">
        {width < 100 ? name?.slice(0, 10) : name?.slice(0, 20)}
      </text>
      {height > 40 && (
        <text x={x + 6} y={y + 30} fontSize={10} fill="rgba(255,255,255,0.8)">
          {fees}
        </text>
      )}
    </g>
  );
}

function AccountTreemap({
  accounts,
  isLoading,
}: {
  accounts: Array<{
    orgName: string | null;
    partner: string | null;
    estimatedFees: number;
  }>;
  isLoading: boolean;
}) {
  const { t } = useTranslation('analytics');
  const data = useMemo(() => {
    return accounts
      .filter(a => a.estimatedFees > 0)
      .slice(0, 30)
      .map(a => ({
        name: a.orgName ?? t('adminRevenue.unknown'),
        size: a.estimatedFees,
        fees: formatMoney(a.estimatedFees, 'USD'),
        fill: partnerAccountColor(a.partner),
      }));
  }, [accounts, t]);

  if (isLoading) return <Skeleton className="h-[300px] w-full" />;
  if (data.length === 0) {
    return <div className="text-muted-foreground flex h-[300px] items-center justify-center text-sm">{t('adminRevenue.noData')}</div>;
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <Treemap
        data={data}
        dataKey="size"
        aspectRatio={4 / 3}
        content={<TreemapContent x={0} y={0} width={0} height={0} name="" fees="" fill="" />}
      />
    </ResponsiveContainer>
  );
}

// --- Sortable Accounts Table ---
type SortField = 'estimatedFees' | 'totalVolume' | 'approvedCount' | 'declinedCount' | 'subclubCount' | 'enhancedFees' | 'approvalRate' | 'orgName';
type SortDir = 'asc' | 'desc';

interface AccountRow {
  accountId: string;
  orgName: string | null;
  orgSlug: string | null;
  partner: string | null;
  approvedCount: number;
  declinedCount: number;
  subclubCount: number;
  totalVolume: string;
  estimatedFees: number;
  enhancedFees: number;
  approvedPayments?: number;
  totalPayments?: number;
}

const PAGE_SIZE = 25;

function SortIcon({ field, sortField, sortDir }: { field: SortField; sortField: SortField; sortDir: SortDir }) {
  if (field !== sortField) return <ArrowUpDown className="ml-1 inline h-3 w-3 opacity-40" />;
  return sortDir === 'asc'
    ? <ArrowUp className="ml-1 inline h-3 w-3" />
    : <ArrowDown className="ml-1 inline h-3 w-3" />;
}

function AllAccountsTable({
  accounts,
  isLoading,
}: {
  accounts: AccountRow[];
  isLoading: boolean;
}) {
  const { t } = useTranslation('analytics');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('estimatedFees');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(0);

  const toggleSort = useCallback((field: SortField) => {
    setSortField(prev => {
      if (prev === field) {
        setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        return field;
      }
      setSortDir('desc');
      return field;
    });
    setPage(0);
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    let rows = accounts;
    if (q) {
      rows = rows.filter(a =>
        (a.orgName ?? '').toLowerCase().includes(q) ||
        (a.orgSlug ?? '').toLowerCase().includes(q) ||
        a.accountId.toLowerCase().includes(q) ||
        (a.partner ?? '').toLowerCase().includes(q)
      );
    }
    rows = [...rows].sort((a, b) => {
      let av: number, bv: number;
      if (sortField === 'orgName') {
        const cmp = (a.orgName ?? '').localeCompare(b.orgName ?? '');
        return sortDir === 'asc' ? cmp : -cmp;
      }
      if (sortField === 'totalVolume') {
        av = parseFloat(a.totalVolume);
        bv = parseFloat(b.totalVolume);
      } else if (sortField === 'approvalRate') {
        av = (a.totalPayments ?? 0) > 0 ? ((a.approvedPayments ?? 0) / a.totalPayments!) : 0;
        bv = (b.totalPayments ?? 0) > 0 ? ((b.approvedPayments ?? 0) / b.totalPayments!) : 0;
      } else {
        av = (a as any)[sortField] ?? 0;
        bv = (b as any)[sortField] ?? 0;
      }
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return rows;
  }, [accounts, search, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const totals = useMemo(() => {
    return filtered.reduce(
      (acc, a) => ({
        volume: acc.volume + parseFloat(a.totalVolume),
        approved: acc.approved + a.approvedCount,
        subclub: acc.subclub + a.subclubCount,
        declined: acc.declined + a.declinedCount,
        enhanced: acc.enhanced + a.enhancedFees,
        fees: acc.fees + a.estimatedFees,
        approvedPayments: acc.approvedPayments + (a.approvedPayments ?? 0),
        totalPayments: acc.totalPayments + (a.totalPayments ?? 0),
      }),
      { volume: 0, approved: 0, subclub: 0, declined: 0, enhanced: 0, fees: 0, approvedPayments: 0, totalPayments: 0 },
    );
  }, [filtered]);

  const totalRate = totals.totalPayments > 0
    ? (totals.approvedPayments / totals.totalPayments) * 100
    : 0;

  const thClass = 'cursor-pointer select-none hover:bg-muted/50 transition-colors';

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-lg">{t('adminRevenue.allAccountsByFees')}</CardTitle>
            <CardDescription>
              {t('adminRevenue.sortableSearchable')}
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                placeholder={t('adminRevenue.searchAccounts')}
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="w-[240px] pl-9"
              />
            </div>
            <span className="text-muted-foreground text-xs whitespace-nowrap">
              {t('adminRevenue.ofAccounts', { filtered: filtered.length, total: accounts.length })}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : accounts.length === 0 ? (
          <p className="text-muted-foreground py-8 text-center text-sm">{t('adminRevenue.noAccountData')}</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">#</TableHead>
                    <TableHead className={thClass} onClick={() => toggleSort('orgName')}>
                      {t('adminRevenue.account')} <SortIcon field="orgName" sortField={sortField} sortDir={sortDir} />
                    </TableHead>
                    <TableHead>{t('adminRevenue.partner')}</TableHead>
                    <TableHead className={`text-right ${thClass}`} onClick={() => toggleSort('totalVolume')}>
                      {t('adminRevenue.volume')} <SortIcon field="totalVolume" sortField={sortField} sortDir={sortDir} />
                    </TableHead>
                    <TableHead className={`text-right ${thClass}`} onClick={() => toggleSort('approvedCount')}>
                      {t('adminRevenue.approved')} <SortIcon field="approvedCount" sortField={sortField} sortDir={sortDir} />
                    </TableHead>
                    <TableHead className={`text-right ${thClass}`} onClick={() => toggleSort('subclubCount')}>
                      {t('adminRevenue.subclub')} <SortIcon field="subclubCount" sortField={sortField} sortDir={sortDir} />
                    </TableHead>
                    <TableHead className={`text-right ${thClass}`} onClick={() => toggleSort('declinedCount')}>
                      {t('adminRevenue.declined')} <SortIcon field="declinedCount" sortField={sortField} sortDir={sortDir} />
                    </TableHead>
                    <TableHead className={`text-right ${thClass}`} onClick={() => toggleSort('approvalRate')}>
                      {t('adminRevenue.rate')} <SortIcon field="approvalRate" sortField={sortField} sortDir={sortDir} />
                    </TableHead>
                    <TableHead className={`text-right ${thClass}`} onClick={() => toggleSort('enhancedFees')}>
                      {t('adminRevenue.enhanced')} <SortIcon field="enhancedFees" sortField={sortField} sortDir={sortDir} />
                    </TableHead>
                    <TableHead className={`text-right ${thClass}`} onClick={() => toggleSort('estimatedFees')}>
                      {t('adminRevenue.totalFees')} <SortIcon field="estimatedFees" sortField={sortField} sortDir={sortDir} />
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageRows.map((account, idx) => {
                    const rate = (account.totalPayments ?? 0) > 0
                      ? ((account.approvedPayments ?? 0) / account.totalPayments! * 100)
                      : 0;
                    return (
                      <TableRow key={account.accountId}>
                        <TableCell className="text-muted-foreground font-mono text-xs">
                          {page * PAGE_SIZE + idx + 1}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">
                              {account.orgName ?? account.accountId.slice(0, 15)}
                            </span>
                            <span className="text-muted-foreground font-mono text-xs">
                              {account.orgSlug ?? account.accountId.slice(0, 18)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={account.partner && account.partner !== 'tagada' ? 'outline' : 'default'} className="text-xs">
                            {account.partner ?? 'tagada'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm tabular-nums">
                          ${parseFloat(account.totalVolume).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{account.approvedCount.toLocaleString()}</TableCell>
                        <TableCell className="text-right tabular-nums">{account.subclubCount.toLocaleString()}</TableCell>
                        <TableCell className="text-right tabular-nums">{account.declinedCount.toLocaleString()}</TableCell>
                        <TableCell className={`text-right font-semibold tabular-nums ${getApprovalRateColor(rate)}`}>
                          {rate.toFixed(1)}%
                        </TableCell>
                        <TableCell className="text-muted-foreground text-right text-xs tabular-nums">
                          {formatMoney(account.enhancedFees, 'USD')}
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">
                          {formatMoney(account.estimatedFees, 'USD')}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Totals row */}
                  <TableRow className="bg-muted/50 font-medium">
                    <TableCell />
                    <TableCell className="text-sm font-semibold">
                      {t('adminRevenue.totalAccounts', { count: filtered.length })}
                    </TableCell>
                    <TableCell />
                    <TableCell className="text-right text-sm tabular-nums">
                      ${totals.volume.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{totals.approved.toLocaleString()}</TableCell>
                    <TableCell className="text-right tabular-nums">{totals.subclub.toLocaleString()}</TableCell>
                    <TableCell className="text-right tabular-nums">{totals.declined.toLocaleString()}</TableCell>
                    <TableCell className={`text-right font-semibold tabular-nums ${getApprovalRateColor(totalRate)}`}>
                      {totalRate.toFixed(1)}%
                    </TableCell>
                    <TableCell className="text-right text-xs tabular-nums">
                      {formatMoney(totals.enhanced, 'USD')}
                    </TableCell>
                    <TableCell className="text-right font-bold tabular-nums">
                      {formatMoney(totals.fees, 'USD')}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-between">
                <span className="text-muted-foreground text-sm">
                  {t('adminRevenue.pageOf', { page: page + 1, total: totalPages })}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(0, p - 1))}
                    disabled={page === 0}
                  >
                    <ChevronLeft className="mr-1 h-4 w-4" /> {t('adminRevenue.previous')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    {t('adminRevenue.next')} <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

// --- Main Page ---

function computeWeeks(range: DateRange): number {
  if (!range.from || !range.to) return 12;
  const diffMs = range.to.getTime() - range.from.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  return Math.max(1, Math.ceil(diffDays / 7));
}

export default function AdminRevenuePage() {
  // Revenue mix references tagada-internal sub-products (FMJ, Apptics whitelabel, MoR);
  // it has no meaning on whitelabel deploys, so route away to avoid leaking the strings.
  if (!isTagadaPay) {
    return <Navigate to="/" replace />;
  }

  const [dateRange, setDateRange] = useUrlDateRange(getParisCurrentWeek);
  const periodStart = dateRange.from;
  const periodEnd = dateRange.to;

  const weeks = useMemo(() => computeWeeks(dateRange), [dateRange]);

  const { data, isLoading, error, refetch } = trpc.admin.revenueEstimate.useQuery(
    { periodStart, periodEnd },
    { staleTime: 2 * 60 * 1000, refetchOnWindowFocus: false },
  );
  const { data: historyData, isLoading: historyLoading } = trpc.admin.revenueHistory.useQuery(
    { weeks: Math.min(weeks, 52), periodStart: periodStart ?? undefined, periodEnd: periodEnd ?? undefined },
    { staleTime: 5 * 60 * 1000, refetchOnWindowFocus: false },
  );
  const backfillOrgNames = trpc.admin.backfillOrgNames.useMutation({
    onSuccess: () => refetch(),
  });

  const analyticsFilters = useMemo(() => ({
    startDate: periodStart ?? new Date(),
    endDate: periodEnd ?? new Date(),
    storeIds: [] as string[],
  }), [periodStart, periodEnd]);

  const queryOpts = { staleTime: 2 * 60 * 1000, refetchOnWindowFocus: false };
  const straightSale = trpc.admin.globalStraightSale.useQuery(analyticsFilters, queryOpts);
  const rebillRevenue = trpc.admin.globalRebillRevenue.useQuery(analyticsFilters, queryOpts);
  const upsellRevenue = trpc.admin.globalUpsellRevenue.useQuery(analyticsFilters, queryOpts);
  const mixLoading = straightSale.isLoading || rebillRevenue.isLoading || upsellRevenue.isLoading;

  const { t } = useTranslation(['analytics', 'common']);

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="py-8 text-center">
          <p className="text-destructive font-medium">{t('analytics:adminRevenue.failedToLoadRevenue')}</p>
          <p className="text-muted-foreground mt-1 text-sm">{error.message}</p>
          <Button variant="outline" size="sm" className="mt-4" onClick={() => refetch()}>
            {t('common:retry')}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const tagadaRevenue = data?.tagadaRevenue;
  // Sources 6 + 7 (added 2026-09-28): read defensively so the page works
  // with an older generated trpc-types bundle.
  const extraRevenue = (tagadaRevenue ?? {}) as { adyenNetCents?: number; eventFeesCents?: number };
  const globalTotals = data?.globalTotals;
  const hasUnresolvedNames = data?.accountEstimates?.some(a => !a.orgName || a.orgName === a.accountId);

  return (
    <div className="space-y-6">
      {/* Time Range Picker */}
      <div className="flex items-center gap-4">
        <DateRangePicker value={dateRange} onChange={setDateRange} />
        <span className="text-muted-foreground text-xs">
          {periodStart ? formatParisDate(periodStart, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
          {' — '}
          {periodEnd ? formatParisDate(periodEnd, { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
          {' '}
          {t('adminRevenue.parisTime')}
        </span>
      </div>

      {/* Hero — Net Tagada Revenue with inline period + partner deductions */}
      <div className="rounded-xl border bg-gradient-to-br from-emerald-50/50 via-background to-background dark:from-emerald-950/20 p-6">
        <div className="grid gap-6 md:grid-cols-[2fr,3fr]">
          {/* Left: net revenue + projection */}
          <div className="flex flex-col justify-between gap-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Net Tagada Revenue
              </p>
              {data ? (
                <p className="mt-1 font-semibold tabular-nums text-4xl text-emerald-700 dark:text-emerald-400">
                  {formatMoney(data.netTagadaRevenueCents, 'USD')}
                </p>
              ) : (
                <Skeleton className="mt-2 h-10 w-48" />
              )}
              {data && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Gross{' '}
                  <span className="font-medium text-foreground tabular-nums">
                    {tagadaRevenue ? formatMoney(tagadaRevenue.totalRevenueCents, 'USD') : '—'}
                  </span>{' '}
                  − partner shares{' '}
                  <span className="font-medium text-red-600 tabular-nums">
                    {formatMoney(data.totalPartnerDeductionsCents, 'USD')}
                  </span>
                </p>
              )}
            </div>

            {data && (
              <div className="space-y-2">
                <div className="flex items-baseline justify-between text-xs">
                  <span className="text-muted-foreground">
                    {new Date(data.periodStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {' – '}
                    {new Date(data.periodEnd).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                  <span className="text-foreground tabular-nums font-semibold">
                    {data.progressPercentage.toFixed(0)}% {t('adminRevenue.complete')}
                  </span>
                </div>
                <Progress value={data.progressPercentage} className="h-1.5" />
                {data.progressPercentage < 100 && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">
                      Tracking to{' '}
                      <span className="font-semibold text-foreground tabular-nums">
                        {formatMoney(data.projectedNetTagadaRevenueCents, 'USD')}
                      </span>{' '}
                      by end of period
                      {data.lowConfidenceProjection && (
                        <span
                          className="ml-2 text-amber-700 dark:text-amber-500"
                          title={`No activity in the most recent complete week — projection falls back to the realized figures`}
                        >
                          • low confidence
                        </span>
                      )}
                    </p>
                    {data.mrr && (
                      <p className="text-xs text-muted-foreground">
                        ≈{' '}
                        <span className="font-semibold text-foreground tabular-nums">
                          {formatMoney(data.mrr.netTagadaCents, 'USD')}
                        </span>{' '}
                        net MRR
                        <span
                          className="ml-1.5 text-muted-foreground/70"
                          title={`Latest complete week's run-rate annualized to a 30.4375-day month (monthly Stripe platform fee amortized separately). Tracks current scale; stable regardless of the day of the month.`}
                        >
                          (run-rate, gross {formatMoney(data.mrr.totalCents, 'USD')})
                        </span>
                      </p>
                    )}
                  </div>
                )}
                {data.progressPercentage >= 100 && data.mrr && (
                  <p className="text-xs text-muted-foreground">
                    ≈{' '}
                    <span className="font-semibold text-foreground tabular-nums">
                      {formatMoney(data.mrr.netTagadaCents, 'USD')}
                    </span>{' '}
                    net MRR
                    <span
                      className="ml-1.5 text-muted-foreground/70"
                      title={`Latest complete week's run-rate annualized to a 30.4375-day month (monthly Stripe platform fee amortized separately).`}
                    >
                      (run-rate, gross {formatMoney(data.mrr.totalCents, 'USD')})
                    </span>
                  </p>
                )}
                {hasUnresolvedNames && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => backfillOrgNames.mutate()}
                    disabled={backfillOrgNames.isPending}
                    className="mt-1"
                  >
                    <RefreshCw className={`mr-2 h-3 w-3 ${backfillOrgNames.isPending ? 'animate-spin' : ''}`} />
                    {t('adminRevenue.syncNames')}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Right: partner shares list (inline, no nested card) */}
          <div className="border-t pt-4 md:border-l md:border-t-0 md:pl-6 md:pt-0">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Partner revenue shares
            </p>
            {data && data.partnerDeductions && data.partnerDeductions.length > 0 ? (
              <div className="mt-3 space-y-2">
                {data.partnerDeductions.map((d: any) => (
                  <div
                    key={d.partnerSlug}
                    className="flex items-center justify-between gap-4 border-b border-border/60 pb-2 last:border-0 last:pb-0"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{d.partnerLabel}</p>
                      <p className="text-muted-foreground text-xs truncate">
                        CRM −{formatMoney(d.crmShareCents, 'USD')}
                        {' · '}
                        Processing −{formatMoney(d.processingShareCents, 'USD')}
                      </p>
                    </div>
                    <span className="font-semibold text-red-600 tabular-nums whitespace-nowrap">
                      −{formatMoney(d.totalDeductionCents, 'USD')}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between pt-1">
                  <span className="text-sm font-semibold">Total deductions</span>
                  <span className="font-bold text-red-600 tabular-nums">
                    −{formatMoney(data.totalPartnerDeductionsCents, 'USD')}
                  </span>
                </div>
              </div>
            ) : isLoading ? (
              <div className="mt-3 space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-7 w-full" />)}
              </div>
            ) : (
              <p className="mt-3 text-sm text-muted-foreground">No partner deductions this period.</p>
            )}
          </div>
        </div>
      </div>

      {/* Revenue by Source — denser strip, no nested cards */}
      <section className="space-y-3">
        <div className="flex items-baseline gap-3">
          <h2 className="text-base font-semibold">{t('adminRevenue.revenueBySource')}</h2>
          <p className="text-sm text-muted-foreground">
            Where each dollar of gross revenue came from this period
          </p>
        </div>
        <div className="rounded-lg border divide-x divide-border flex flex-wrap [&>div]:flex-1 [&>div]:min-w-[180px]">
          {[
            {
              label: t('adminRevenue.tagadaPayCustomers'),
              value: tagadaRevenue ? formatMoney(tagadaRevenue.tagadaCustomerFeesCents, 'USD') : '—',
              sub: t('adminRevenue.tagadaPaySubtitle'),
              dot: REVENUE_COLORS.tagadaPay,
            },
            {
              label: t('adminRevenue.resellerOnTagadaPay'),
              value: tagadaRevenue ? formatMoney(tagadaRevenue.fmjRevenueCents, 'USD') : '—',
              sub: tagadaRevenue
                ? t('adminRevenue.resellerSubtitle', { amount: formatMoney(dollarsToCents(tagadaRevenue.fmjVolumeDollars), 'USD') })
                : '',
              dot: REVENUE_COLORS.reseller,
            },
            {
              label: t('adminRevenue.whitelabelDeploy'),
              value: tagadaRevenue ? formatMoney(tagadaRevenue.appticsRevenueCents, 'USD') : '—',
              sub: tagadaRevenue
                ? t('adminRevenue.whitelabelDeploySubtitle', { amount: formatMoney(dollarsToCents(tagadaRevenue.appticsVolumeDollars), 'USD') })
                : '',
              dot: REVENUE_COLORS.whitelabelDeploy,
            },
            {
              label: t('adminRevenue.tagadaPaymentMoR'),
              // Partner-scoped queries return null (Q6 Option C); fall
              // back to gross app fees with a "(gross)" footnote so
              // partners aren't shown a fake "net" number.
              value: tagadaRevenue
                ? tagadaRevenue.morNetFeesCents !== null
                  ? formatMoney(tagadaRevenue.morNetFeesCents, 'USD')
                  : `${formatMoney(dollarsToCents(tagadaRevenue.morGrossFeesDollars), 'USD')} (gross)`
                : '—',
              sub: tagadaRevenue
                ? t('adminRevenue.morSubtitle', { amount: formatMoney(dollarsToCents(tagadaRevenue.morVolumeDollars), 'USD') })
                : '',
              dot: REVENUE_COLORS.mor,
            },
            ...(tagadaRevenue && tagadaRevenue.partnerResoldCrmCents > 0
              ? [{
                  label: 'Partner CRM (resold)',
                  value: formatMoney(tagadaRevenue.partnerResoldCrmCents, 'USD'),
                  sub: 'Revenue-share partners (gross; their shares appear in Partner Deductions)',
                  dot: REVENUE_COLORS.partnerCrm,
                }]
              : []),
            ...((extraRevenue.adyenNetCents ?? 0) !== 0
              ? [{
                  label: 'Adyen (net)',
                  value: formatMoney(extraRevenue.adyenNetCents ?? 0, 'USD'),
                  sub: 'Our commission minus Adyen costs on the Tagada account',
                  dot: REVENUE_COLORS.adyen,
                }]
              : []),
            ...((extraRevenue.eventFeesCents ?? 0) !== 0
              ? [{
                  label: 'Event fees',
                  value: formatMoney(extraRevenue.eventFeesCents ?? 0, 'USD'),
                  sub: 'Alerts, disputes, Adyen events billed, minus Stripe dispute fees',
                  dot: REVENUE_COLORS.eventFees,
                }]
              : []),
            {
              label: t('adminRevenue.activeAccounts'),
              value: globalTotals?.activeAccounts.toString() ?? '—',
              sub: t('adminRevenue.globalApprovalRate', { rate: globalTotals?.approvalRate.toFixed(1) ?? 0 }),
            },
          ].map((row) => (
            <div key={row.label} className="flex flex-col gap-0.5 px-4 py-3 first:pl-4 last:pr-4">
              <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {row.dot && (
                  <span
                    className="inline-block h-2 w-2 rounded-full"
                    style={{ backgroundColor: row.dot }}
                  />
                )}
                {row.label}
              </span>
              <span className="text-xl font-semibold tabular-nums">{row.value}</span>
              {row.sub && <span className="text-xs text-muted-foreground">{row.sub}</span>}
            </div>
          ))}
        </div>
      </section>

      {/* Revenue Over Time */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('adminRevenue.revenueOverTime')}</CardTitle>
          <CardDescription>{t('adminRevenue.weeklyRevenueBySource', { weeks: Math.min(weeks, 52) })}</CardDescription>
        </CardHeader>
        <CardContent>
          <RevenueOverTimeChart
            data={(historyData ?? []) as any}
            isLoading={historyLoading}
          />
        </CardContent>
      </Card>

      {/* Donut + Revenue Mix side by side */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('adminRevenue.revenueComposition')}</CardTitle>
            <CardDescription>{t('adminRevenue.revenueSplitBySource')}</CardDescription>
          </CardHeader>
          <CardContent>
            <RevenueDonut tagadaRevenue={tagadaRevenue} isLoading={isLoading} />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('adminRevenue.revenueMixOverTime')}</CardTitle>
            <CardDescription>{t('adminRevenue.dailyRevenueMix')}</CardDescription>
          </CardHeader>
          <CardContent>
            <RevenueMixChart
              straightSaleData={straightSale.data as MapEntry}
              rebillData={rebillRevenue.data as MapEntry}
              upsellData={upsellRevenue.data as MapEntry}
              isLoading={mixLoading}
            />
          </CardContent>
        </Card>
      </div>

      {/* Partner Breakdown + Global Stats */}
      <div className="grid gap-6 lg:grid-cols-2">
        <PartnerBreakdown
          summaries={data?.partnerSummaries ?? []}
          isLoading={isLoading}
        />
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('adminRevenue.globalTransactionStats')}</CardTitle>
            <CardDescription>{t('adminRevenue.globalStatsDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : globalTotals ? (
              <div className="space-y-3">
                <div className="flex justify-between border-b pb-2">
                  <span className="text-sm">{t('adminRevenue.totalApproved')}</span>
                  <span className="font-semibold">{globalTotals.approved.toLocaleString()}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-sm">{t('adminRevenue.totalSubclub')}</span>
                  <span className="font-semibold">{globalTotals.subclub.toLocaleString()}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-sm">{t('adminRevenue.totalDeclined')}</span>
                  <span className="font-semibold">{globalTotals.declined.toLocaleString()}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-sm">{t('adminRevenue.totalRefunds')}</span>
                  <span className="font-semibold">{globalTotals.refunds.toLocaleString()}</span>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <span className="text-sm">{t('adminRevenue.approvalRate')}</span>
                  <span className="font-semibold">{globalTotals.approvalRate.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm">{t('adminRevenue.subclubRateOfApproved')}</span>
                  <span className="font-semibold">{t('adminRevenue.subclubRateDescription', { rate: globalTotals.subclubRate.toFixed(1) })}</span>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      {/* Account Treemap */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('adminRevenue.accountConcentration')}</CardTitle>
          <CardDescription>{t('adminRevenue.accountConcentrationDescription')}</CardDescription>
        </CardHeader>
        <CardContent>
          <AccountTreemap
            accounts={data?.accountEstimates ?? []}
            isLoading={isLoading}
          />
        </CardContent>
      </Card>

      {/* Full Accounts Table */}
      <AllAccountsTable
        accounts={data?.accountEstimates ?? []}
        isLoading={isLoading}
      />
    </div>
  );
}
